/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Render the CL-F03 invitation management dialog with independently composed Desktop and full-height Mobile states.
 * @state-owner TanStack Query grant projections plus dialog-local form and reveal-once token state
 * @allowed-imports React; classroom grant hooks/models; active context; shared overlay, modal, loading, QR, error and translator helpers
 * @forbidden direct API client; client authorization decisions; secret persistence
 * @public-api classrooms module
 * @side-effects creates, rotates and revokes grant commands; reveals a returned token once for copying
 * @tests ClassroomAccessGrantDialog.test.tsx and classroom-access-grants Playwright flows
 * @security-boundary server-projected grant status controls affordances; backend re-authorizes every command
 * @data-classification invite addresses, projected grant status and one-time plaintext tokens
 * @trust-model form and query projections are untrusted; API is the source of authorization and persistence
 */

import { useState } from 'react';
import { useActiveContextId } from '../../../app/providers/ActiveRoleProvider';
import { ModalDocument } from '../../../shared/components/Modal';
import { useOverlay } from '../../../shared/components/OverlayHost';
import { QrCode } from '../../../shared/components/QrCode';
import { VispoLoading } from '../../../shared/components/WorkspaceState';
import { hasHttpStatus } from '../../../shared/lib/api-error';
import { useIsMobile, useTranslator } from '../../../shared/model/use-workspace';
import {
  useCreateClassroomInvitation,
  useCreateStudentJoinLink,
  useDeleteClassroomInvitation,
  useSendClassroomInvitation,
  useUpdateStudentJoinLink,
} from '../api/classrooms.mutations';
import { useClassroomInvitations, useStudentJoinLinks } from '../api/classrooms.queries';
import type { ClassroomAccessGrant, StudentJoinLinkSummary } from '../model/access-grants';
import './classroom-access-grants.css';

function commandKey() {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `agent-b-cl-f03-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
}

function defaultExpiry() {
  return new Date(Date.now() + 1000 * 60 * 60 * 24 * 30).toISOString().slice(0, 16);
}

function expiryIso(value: string) {
  return new Date(value).toISOString();
}

function grantLink(grant: ClassroomAccessGrant) {
  return grant.token ? `${window.location.origin}/join/${grant.token}` : null;
}

function statusLabel(grant: ClassroomAccessGrant, t: (value: string) => string) {
  if (grant.status === 'accepted') return t('已加入');
  if (grant.status === 'expired') return t('已過期');
  if (grant.status === 'revoked') return t('已撤銷');
  if (grant.status === 'declined') return t('已拒絕');
  return t('待加入');
}

function GrantRow({
  grant,
  onResend,
  onRevoke,
  onRotate,
}: {
  grant: ClassroomAccessGrant;
  onResend(grant: ClassroomAccessGrant): void;
  onRevoke(grant: ClassroomAccessGrant): void;
  onRotate(grant: ClassroomAccessGrant): void;
}) {
  const t = useTranslator();
  const invitation = grant.kind === 'invitation';
  return (
    <li className="classroom-access-grant-row">
      <div>
        <strong>{invitation ? grant.recipientEmail : t('學生 QR 連結')}</strong>
        <span>{`${invitation ? (grant.grantRole === 'teacher' ? t('共同老師') : t('學生')) : t('可重複使用')} · ${statusLabel(grant, t)}`}</span>
      </div>
      <div className="classroom-access-grant-row__actions">
        {invitation && grant.status === 'pending' ? (
          <>
            <button className="bt sm" type="button" onClick={() => onResend(grant)}>
              {t('重新顯示連結')}
            </button>
            <button className="bt sm" type="button" onClick={() => onRevoke(grant)}>
              {t('撤銷')}
            </button>
          </>
        ) : null}
        {!invitation && grant.status !== 'revoked' ? (
          <button className="bt sm" type="button" onClick={() => onRotate(grant)}>
            {t('重置 QR')}
          </button>
        ) : null}
      </div>
    </li>
  );
}

function joinLinkStatusLabel(
  status: StudentJoinLinkSummary['status'],
  t: (value: string) => string,
) {
  if (status === 'expired') return t('已過期');
  if (status === 'revoked') return t('已撤銷');
  return t('可使用');
}

function formatJoinLinkTimestamp(iso: string) {
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return iso;
  return new Intl.DateTimeFormat('zh-TW', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function JoinLinkRow({
  link,
  rotating,
  onRotate,
}: {
  link: StudentJoinLinkSummary;
  rotating: boolean;
  onRotate(link: StudentJoinLinkSummary): void;
}) {
  const t = useTranslator();
  const usage =
    link.maxUses == null
      ? `${link.useCount} ${t('次使用')}`
      : `${link.useCount}/${link.maxUses} ${t('次使用')}`;
  return (
    <li className="classroom-access-grant-row">
      <div>
        <strong>{t('學生 QR 連結')}</strong>
        <span>{`${joinLinkStatusLabel(link.status, t)} · ${t('版本')} ${link.revision} · ${usage}`}</span>
        <span>{`${t('到期')} ${formatJoinLinkTimestamp(link.expiresAt)} · ${t('最後更新')} ${formatJoinLinkTimestamp(link.updatedAt)}`}</span>
      </div>
      <div className="classroom-access-grant-row__actions">
        {link.status !== 'revoked' ? (
          <button
            aria-label={t('重新產生這個學生加入連結')}
            className="bt sm"
            disabled={rotating}
            type="button"
            onClick={() => onRotate(link)}
          >
            {t('重新產生連結')}
          </button>
        ) : null}
      </div>
    </li>
  );
}

export function ClassroomAccessGrantDialog({ classroomId }: { classroomId: string }) {
  const t = useTranslator();
  const mobile = useIsMobile();
  const activeContextId = useActiveContextId();
  const { closeModal } = useOverlay();
  const invitations = useClassroomInvitations(activeContextId, classroomId, 'all');
  const joinLinks = useStudentJoinLinks(activeContextId, classroomId);
  const createInvitationMutation = useCreateClassroomInvitation();
  const createJoinLink = useCreateStudentJoinLink();
  const resend = useSendClassroomInvitation();
  const revoke = useDeleteClassroomInvitation();
  const rotate = useUpdateStudentJoinLink();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'student' | 'teacher'>('student');
  const [expiresAt, setExpiresAt] = useState(defaultExpiry);
  const [revealedGrant, setRevealedGrant] = useState<ClassroomAccessGrant | null>(null);
  const [createdJoinLink, setCreatedJoinLink] = useState<Pick<
    ClassroomAccessGrant,
    'id' | 'revision'
  > | null>(null);
  const [revealedLabel, setRevealedLabel] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const runResult = (result: ClassroomAccessGrant | null, label: string) => {
    if (result) {
      setRevealedGrant(result);
      setRevealedLabel(label);
      if (result.kind === 'student_join_link') {
        setCreatedJoinLink({ id: result.id, revision: result.revision });
      }
    }
  };

  const submitInvitation = async () => {
    if (!email.trim()) {
      setLocalError(t('請輸入 Email'));
      return;
    }
    setLocalError(null);
    try {
      const result = await createInvitationMutation.mutateAsync({
        activeContextId,
        classroomId,
        recipientEmail: email.trim(),
        grantRole: role,
        expiresAt: expiryIso(expiresAt),
        idempotencyKey: commandKey(),
      });
      runResult(result, t('定向邀請連結'));
      setEmail('');
    } catch {
      setLocalError(t('邀請無法建立，請檢查班級權限或現有邀請狀態。'));
    }
  };

  const createLink = async () => {
    setLocalError(null);
    try {
      const result = await createJoinLink.mutateAsync({
        activeContextId,
        classroomId,
        expiresAt: expiryIso(expiresAt),
        idempotencyKey: commandKey(),
      });
      runResult(result, t('學生 QR 連結'));
    } catch {
      setLocalError(t('QR 連結無法建立，請稍後再試。'));
    }
  };

  const error =
    localError ??
    (invitations.isError
      ? hasHttpStatus(invitations.error, 403)
        ? t('你沒有管理這個班級的權限。')
        : t('邀請清單暫時無法載入。')
      : null);
  const fields = (
    <>
      <div className="classroom-access-grant-create">
        <div className="classroom-access-grant-create__heading">
          <strong>{t('新增加入方式')}</strong>
          <span>{t('共同老師只能使用定向 Email；學生也可以使用 QR。')}</span>
        </div>
        <label>
          <span>{t('Email 邀請')}</span>
          <input
            aria-label={t('受邀者 Email')}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
        <label>
          <span>{t('加入角色')}</span>
          <select
            aria-label={t('加入角色')}
            value={role}
            onChange={(event) => setRole(event.target.value as 'student' | 'teacher')}
          >
            <option value="student">{t('學生')}</option>
            <option value="teacher">{t('共同老師')}</option>
          </select>
        </label>
        <label>
          <span>{t('有效期限')}</span>
          <input
            aria-label={t('有效期限')}
            type="datetime-local"
            value={expiresAt}
            onChange={(event) => setExpiresAt(event.target.value)}
          />
        </label>
        <div className="classroom-access-grant-create__buttons">
          <button
            className="bt pri"
            disabled={createInvitationMutation.isPending}
            type="button"
            onClick={() => void submitInvitation()}
          >
            {t('建立 Email 邀請')}
          </button>
          <button
            className="bt"
            disabled={createJoinLink.isPending}
            type="button"
            onClick={() => void createLink()}
          >
            {t('建立學生 QR')}
          </button>
        </div>
      </div>
      {revealedGrant?.token ? (
        <section className="classroom-access-grant-reveal" aria-label={t('一次性連結')}>
          <strong>{`${revealedLabel} · ${t('只顯示一次')}`}</strong>
          <input readOnly aria-label={t('可複製連結')} value={grantLink(revealedGrant) ?? ''} />
          {revealedGrant.kind === 'student_join_link' ? (
            <QrCode seed={grantLink(revealedGrant) ?? ''} />
          ) : null}
          <button
            className="bt sm"
            type="button"
            onClick={() => void navigator.clipboard?.writeText(grantLink(revealedGrant) ?? '')}
          >
            {t('複製連結')}
          </button>
        </section>
      ) : null}
      {createdJoinLink && revealedGrant?.kind === 'student_join_link' ? (
        <section className="classroom-access-grant-join-link" aria-label={t('目前的學生 QR 連結')}>
          <span>{`${t('連結')} · ${t('版本')} ${createdJoinLink.revision}`}</span>
          <button
            className="bt sm"
            type="button"
            onClick={() =>
              void rotate
                .mutateAsync({
                  activeContextId,
                  classroomId,
                  joinLinkId: createdJoinLink.id,
                  expectedRevision: createdJoinLink.revision,
                  idempotencyKey: commandKey(),
                })
                .then((result) => runResult(result, t('重新顯示的 QR 連結')))
            }
          >
            {t('重置 QR')}
          </button>
        </section>
      ) : null}
      {error ? (
        <p className="classroom-access-grant-error" role="alert">
          {error}
        </p>
      ) : null}
      <section className="classroom-access-grant-list" aria-label={t('加入方式清單')}>
        <header>
          <strong>{t('目前的邀請')}</strong>
          <span>{t('待加入與已加入分開顯示')}</span>
        </header>
        {invitations.isLoading ? <VispoLoading label={t('正在載入邀請…')} /> : null}
        {!invitations.isLoading && !invitations.isError && invitations.data?.length === 0 ? (
          <p className="classroom-access-grant-empty">{t('目前沒有邀請')}</p>
        ) : null}
        <ul>
          {invitations.data?.map((grant) => (
            <GrantRow
              grant={grant}
              key={grant.id}
              onResend={(item) => {
                void resend
                  .mutateAsync({
                    activeContextId,
                    classroomId,
                    invitationId: item.id,
                    expectedRevision: item.revision,
                    idempotencyKey: commandKey(),
                  })
                  .then((result) => runResult(result, t('重新顯示的邀請連結')));
              }}
              onRevoke={(item) => {
                void revoke.mutateAsync({
                  activeContextId,
                  classroomId,
                  invitationId: item.id,
                  expectedRevision: item.revision,
                  idempotencyKey: commandKey(),
                });
              }}
              onRotate={(item) => {
                void rotate
                  .mutateAsync({
                    activeContextId,
                    classroomId,
                    joinLinkId: item.id,
                    expectedRevision: item.revision,
                    idempotencyKey: commandKey(),
                  })
                  .then((result) => runResult(result, t('重新顯示的 QR 連結')));
              }}
            />
          ))}
        </ul>
      </section>
      <section className="classroom-access-grant-list" aria-label={t('學生加入連結清單')}>
        <header>
          <strong>{t('目前的學生加入連結')}</strong>
          <span>
            {t(
              '每個連結的網址只在建立或重新產生時顯示一次，這裡的清單無法還原已顯示過的舊網址；需要分享時請點選「重新產生連結」取得新的網址。',
            )}
          </span>
        </header>
        {joinLinks.isLoading ? <VispoLoading label={t('正在載入學生加入連結…')} /> : null}
        {joinLinks.isError ? (
          <p className="classroom-access-grant-error" role="alert">
            {hasHttpStatus(joinLinks.error, 403)
              ? t('你沒有管理這個班級的權限。')
              : t('學生加入連結清單暫時無法載入。')}
          </p>
        ) : null}
        {!joinLinks.isLoading && !joinLinks.isError && joinLinks.data?.length === 0 ? (
          <p className="classroom-access-grant-empty">
            {t('目前沒有學生加入連結，請先建立一個。')}
          </p>
        ) : null}
        <ul>
          {joinLinks.data?.map((link) => (
            <JoinLinkRow
              key={link.id}
              link={link}
              rotating={rotate.isPending}
              onRotate={(item) => {
                void rotate
                  .mutateAsync({
                    activeContextId,
                    classroomId,
                    joinLinkId: item.id,
                    expectedRevision: item.revision,
                    idempotencyKey: commandKey(),
                  })
                  .then((result) => runResult(result, t('重新顯示的 QR 連結')));
              }}
            />
          ))}
        </ul>
      </section>
    </>
  );

  const footer = (
    <button className="bt" type="button" onClick={closeModal}>
      {t('完成')}
    </button>
  );
  return (
    <div className="classroom-access-grant-dialog">
      {mobile ? (
        <section className="classroom-access-grant-surface classroom-access-grant-surface--mobile">
          <ModalDocument
            closeLabel={t('關閉')}
            dialogLabel={t('管理班級邀請')}
            footer={footer}
            maxWidth={720}
            subtitle={t('建立、重新顯示或撤銷班級加入方式。')}
            title={t('班級邀請管理')}
          >
            {fields}
          </ModalDocument>
        </section>
      ) : (
        <section className="classroom-access-grant-surface classroom-access-grant-surface--desktop">
          <ModalDocument
            closeLabel={t('關閉')}
            dialogLabel={t('管理班級邀請')}
            footer={footer}
            maxWidth={720}
            subtitle={t('建立、重新顯示或撤銷班級加入方式。')}
            title={t('班級邀請管理')}
          >
            {fields}
          </ModalDocument>
        </section>
      )}
    </div>
  );
}
